<template>
  <v-row
    id="home-view"
    align="start"
    no-gutters
  >
    <v-col>
      <ChooseCardMenu />
      <DashTopBar />
      <DashCardsContainer />
      <AddCard />
      <PublicAccessModal
        v-if="publicAccessModal"
        @cancel="publicAccessAction"
        @confirm="publicAccessAction"
      />
    </v-col>
  </v-row>
</template>

<script>
import ChooseCardMenu from '@/components/ChooseCardMenu';
import DashTopBar from '@/components/dashboard/DashTopBar';
import DashCardsContainer from '@/components/dashboard/DashCardsContainer';
import AddCard from '@/components/AddCard';
import PublicAccessModal from '@/components/PublicAccessModal';
import store from '@/store/store';
import { checkForAuthentication } from '@stilingue/st-auth-utils';
import { MUTATE_MULTIPLE } from '@/store/modules/mutation_types';
import { mapActions, mapGetters } from 'vuex';
import { insertLogRocketMetadata } from '@/plugins/logrocket';
import { insertAnalyticsMetadata } from '@/plugins/analytics';
import { insertSentryMetadata } from '@/plugins/sentry';
import { insertRolloutMetadata, Flags } from '@/plugins/rollout';
import { insertAppcuesPlugin, GUEST_VALUE } from '@/plugins/appcues';
import { insertMixpanelPlugin, getMixpanelInstance } from '@/plugins/mixpanel';
import { insertHotjarPlugin, GUEST_USER } from '@/plugins/hotjar';
import { loadZendeskScript } from '@/plugins/zendesk';
import { getErrorPage, PAGES_ERROR_TYPES } from '@/router';
import { NORMALIZED_GMT_TIME_ZONE } from '@/utils/date';
import { USER_TYPE, ONBOARDING_STATUS, DASHBOARD_MODELS } from '@/utils/constants';
import { splitStringOnSeparator } from '@/utils/formatter';
import moment from 'moment';
import Cookies from 'js-cookie';

const loadEnv = async (accountId, universeId) => {
  store.commit(`filter/${MUTATE_MULTIPLE}`, [
    {
      property: 'accountId',
      with: accountId,
    },
    {
      property: 'universeId',
      with: universeId,
    },
  ]);

  await store.dispatch('dashboard/getIfUniverseIsStaging', universeId);
};

const loadUser = async ({ accountId, universeId, dashboardId }) => {
  if (!checkForAuthentication()) {
    insertMixpanelPlugin({
      universeId,
      accountId,
    });

    insertAppcuesPlugin({
      account_id: accountId,
      universe_id: universeId,
      user_id: GUEST_VALUE,
      user_type: USER_TYPE.GUEST,
      studio: true,
    });

    insertRolloutMetadata({
      account_id: accountId,
      universe_id: universeId,
      dashboard_id: dashboardId,
      user_email: 'guest@stilingue.com.br',
      user_name: USER_TYPE.GUEST,
      user_id: GUEST_VALUE,
      user_group: 'GUEST',
    });

    insertHotjarPlugin({
      account_id: accountId,
      universe_id: universeId,
      user_id: GUEST_USER,
      user_type: USER_TYPE.GUEST,
      studio: true,
    });

    return;
  }

  // Chamada CRITICA: se o usuario nao puder ser carregado, a sessao e invalida.
  const user = await store.dispatch('user/getUserMe');
  if (!user) {
    return;
  }

  // Chamadas SECUNDARIAS: apenas para enriquecer plugins de analytics.
  // Falhas aqui NAO devem bloquear o acesso nem deslogar o usuario.
  let account = null;
  let universeName = null;
  let userGroup = null;

  try {
    account = await store.dispatch('account/getAccount', accountId);
  } catch (e) {
    console.warn('[loadUser] Failed to load account (non-critical):');
  }

  try {
    universeName = await store.dispatch('universe/getUniverseName', { accountId, universeId });
  } catch (e) {
    console.warn('[loadUser] Failed to load universeName (non-critical):');
  }

  try {
    userGroup = await store.dispatch('userGroup/getUserGroup', user.userGroup);
  } catch (e) {
    console.warn('[loadUser] Failed to load userGroup (non-critical):');
  }

  insertLogRocketMetadata({
    email: user.email,
    name: user.name,
    id: user.id,
  });

  insertAnalyticsMetadata({
    user_email: user.email,
    user_name: user.name,
    user_id: user.id,
    user_group: user.userGroup,
  });

  insertSentryMetadata({
    username: user.name,
    email: user.email,
    id: user.id,
  });

  insertAppcuesPlugin({
    email: user.email,
    name: user.name,
    created_at: moment(user.createdAt).format(NORMALIZED_GMT_TIME_ZONE),
    user_id: user.id,
    master: user.master,
    payment_kind: user.paymentKind,
    user_group: user.userGroup,
    user_admin: user.admin,
    user_type: USER_TYPE.EDITOR,
    account_id: accountId,
    account_name: account?.name,
    account_user_group: account?.userGroup,
    role: user.role,
    pod: userGroup?.pod,
    path_url: window.location.pathname,
    universe_id: universeId,
    universe_name: universeName?.name,
    studio: true,
  });

  insertRolloutMetadata({
    account_id: accountId,
    universe_id: universeId,
    dashboard_id: dashboardId,
    user_email: user.email,
    user_name: user.name,
    user_id: user.id,
    user_group: user.userGroup,
  });

  insertMixpanelPlugin({
    universeId,
    accountId,
    userId: user.id,
    userEmail: user.email,
    userName: user.name,
    admin: user.admin,
    avatarUrl: user.avatarUrl,
    createdAt: user.createdAt,
    emailConfirmed: user.emailConfirmed,
    initialPasswordChanged: user.initialPasswordChanged,
    intercomHash: user.intercomHash,
    paymentKind: user.paymentKind,
    readOnly: user.readOnly,
    role: user.role,
    roleId: user.roleId,
    userGroup: user.userGroup,
  });

  insertHotjarPlugin({
    email: user.email,
    name: user.name,
    created_at: moment(user.createdAt).format(NORMALIZED_GMT_TIME_ZONE),
    user_id: user.id,
    master: user.master,
    payment_kind: user.paymentKind,
    user_group: user.userGroup,
    user_admin: user.admin,
    user_type: USER_TYPE.EDITOR,
    account_id: accountId,
    account_name: account?.name,
    account_user_group: account?.userGroup,
    role: user.role,
    pod: userGroup?.pod,
    path_url: window.location.pathname,
    universe_id: universeId,
    universe_name: universeName?.name,
    studio: true,
  });

  loadZendeskScript ({
    user,
  });

  await store.dispatch('user/getFirebaseUser', user);

  return user;
};

const getDashboards = async (universeId, token) => {
  const dashboards = await store.dispatch('dashboard/getDashboards', {
    universeId,
    token,
  });

  return dashboards;
};

const getIsAdmin = async () => {
  await store.dispatch('user/getIsAdmin');
};

const getUniverseTenants = async (universeId) => {
  await store.dispatch('tenant/getUniverseTenants', universeId);
};

const updateAllowCardsFromDatabase = async () => {
  await store.dispatch('cards/updateAllowCardsFromDatabase');
};

export default {
  name: 'App',
  components: {
    ChooseCardMenu,
    DashTopBar,
    DashCardsContainer,
    AddCard,
    PublicAccessModal,
  },
  data() {
    return {
      defaultDarkThemeValue: true,
      publicAccessModal: false,
      publicUserCookieKey: 'public-user-logged-in',
    };
  },
  computed: {
    ...mapGetters('dashboard', [
      'activeDashboard',
    ]),
    ...mapGetters('featureFlag', [
      'getValue',
    ]),
    ...mapGetters('user', [
      'isEditor',
    ]),
  },
  watch: {
    activeDashboard (newDashboard) {
      if (!newDashboard) {
        const { params, query } = this.$route;
        const name = query.public ? 'token' : 'login';

        this.$router.push({
          name,
          params,
          query,
        });

        return;
      }

      this.updateDashboard();
      getMixpanelInstance().people.set('dashboardId', newDashboard.id);
    },
  },
  mounted() {
    this.updateDashboard();
    localStorage.removeItem('dashboard-token');
    Cookies.remove('dashboard-token');
  },
  async created() {
    const { params } = this.$route;
    const userData = {
      dashboardId: this.activeDashboard.id,
      accountId: params.accountId,
      universeId: params.universeId,
    };

    await this.getUniverseInfo();

    await loadUser(userData);

    const userLoggedIn = Cookies.get(this.publicUserCookieKey);
    const publicDashboardInfo = await this.getValue(Flags.publicDashboardInfo);
    const dashboardTokenId = splitStringOnSeparator(publicDashboardInfo, '|')[0];

    this.publicAccessModal = !(Boolean(userLoggedIn)) && Boolean(dashboardTokenId) && !this.isEditor;
  },
  // eslint-disable-next-line complexity
  async beforeRouteEnter(to, from, next) {
    const { accountId, universeId } = to.params;
    const { public: isPublic, dashboard } = to.query;
    const token = Cookies.get('dashboard-token');

    await loadEnv(accountId, universeId);

    if (isPublic && !token) {
      return next({
        name: 'token',
        params: to.params,
        query: to.query,
      });
    }

    const userData = {
      dashboardId: dashboard || DASHBOARD_MODELS[0].id,
      accountId,
      universeId,
    };

    const user = await loadUser(userData);
    if (!isPublic && !user) {
      return next({
        name: 'login',
        params: to.params,
        query: to.query,
      });
    }

    const dashboards = await getDashboards(universeId, token);

    await getIsAdmin();

    if (!dashboards.length) {
      return next(getErrorPage(PAGES_ERROR_TYPES.DASHBOARD_NOT_FOUND));
    }

    await updateAllowCardsFromDatabase();

    await getUniverseTenants(universeId);

    if (isPublic && token && !user) {
      return next();
    }

    let dashboardsInOnboarding;
    for (const dashboard of dashboards) {
      dashboardsInOnboarding = dashboard.data.users.filter(usr => usr.email === user.email && ![ONBOARDING_STATUS.DONE.key, ONBOARDING_STATUS.SKIP.key].includes(usr.onboardingStatus.key));
    }
    if (dashboardsInOnboarding.length) {
      return next({
        name: 'onboarding',
        params: to.params,
        query: to.query,
      });
    }

    return next();
  },
  methods: {
    ...mapActions('dashboard', [
      'getUniverseInfo',
    ]),
    updateDashboard() {
      if ('isDarkTheme' in this.activeDashboard.data) {
        this.$vuetify.theme.dark = this.activeDashboard.data.isDarkTheme;
      }
    },
    publicAccessAction(event) {
      this.publicAccessModal = event.value;
    },
  },
};
</script>

<style lang="scss" scoped>
</style>
